<template>
    <NuxtLayout name="main">
        <module-navigations />

        <div :class="css.container">
            <ColumnLayout :class="css.content" class="container">
                <!-- * 统计结果 -->
                <div :class="css.header">
                    <div :class="css.statistics" class="font-variable" ref="container">
                        <template v-if="!keyword">{{ $t("Layout.PaginationStatistics", { count: count }) }}</template>
                        <i18n-t v-else scope="global" keypath="SearchResultPlaceholder">
                            <template v-slot:number>{{ count }}</template>
                            <template v-slot:keyword>
                                <span>{{ keyword }}</span>
                            </template>
                        </i18n-t>
                    </div>
                    <div :class="css.searchBox">
                        <UiSelect :class="css.select" width="176px" v-model="gene" @change="methods.data(1)" size="large" :candidates="selector2">
                        </UiSelect>
                        <DefaultSearch :class="css.input" width="304px" v-model="keyword2" @search="methods.data(1)" size="large"> </DefaultSearch>
                    </div>
                </div>

                <!-- * 表格 -->
                <SearchDiagram :class="css.table" v-on="ons.diagram" ref="table" :data="data" :option="option" :placeholder="$t('NoData')">
                    <template #noResult>
                        <div style="text-align: center">
                            <i18n-t keypath="MutationNoResult" scope="global">
                                <template #keyword>
                                    <span :class="css.keyword" class="ellipsis">{{ keyword }}</span>
                                </template>
                                <template #pathogenicity>
                                    <ProjectRoute :class="css_normalize.assets" :to="{ name: 'Pathogenicity' }" target="_blank"
                                        >Pathogenic Predictor</ProjectRoute
                                    >
                                </template>
                                <template #rna_splicer>
                                    <ProjectRoute :class="css_normalize.assets" :to="{ name: 'RnaSplicer' }" target="_blank"
                                        >RNA Splicer</ProjectRoute
                                    >
                                </template>
                                <template #variant_viewer>
                                    <ProjectRoute :class="css_normalize.assets" :to="{ name: 'BioVariant' }" target="_blank"
                                        >Mutation Direct</ProjectRoute
                                    >
                                </template>
                            </i18n-t>
                        </div>
                    </template>

                    <template #gene="{ data }">
                        <project-route
                            :class="css_normalize.assets"
                            :to="{ name: 'Gene', params: { id: data.gene_id }, query: { keyword: keyword } }"
                            v-html="data.gene" />
                    </template>

                    <template #mutation_transcript="{ data }">
                        <template v-if="!data.mutation_transcript">--</template>
                        <template v-else-if="!data.skip">
                            {{ data.mutation_transcript }}
                        </template>
                        <project-route
                            v-else
                            :class="css_normalize.assets"
                            :to="{
                                name: 'BioVariantResult',
                                query: { id: data.transcript, mode: '', keyword: data.mutation_transcript },
                                params: { id: data.gene_id },
                            }">
                            <span> {{ data.mutation_transcript }}</span>
                        </project-route>
                    </template>

                    <template #mutation_protein="{ data }">
                        <template v-if="!data.mutation_protein">--</template>
                        <template v-else-if="!data.skip">{{ data.mutation_protein }}</template>
                        <project-route
                            v-else
                            :class="css_normalize.assets"
                            :to="{
                                name: 'BioVariantResult',
                                query: { id: data.transcript, mode: 'codon', keyword: data.mutation_protein },
                                params: { id: data.gene_id },
                            }">
                            {{ data.mutation_protein }}
                        </project-route>
                    </template>

                    <template #transcript="{ data }">
                        <template v-if="!data.transcript">--</template>
                        <template v-else-if="!data.skip">{{ data.transcript }}</template>
                        <template v-else>
                            <project-route
                                :class="[css.mrna, css_normalize.assets]"
                                :to="{ name: 'BioVariantResult', query: { id: data.transcript, mode: 'codon' }, params: { id: data.gene_id } }">
                                <span>{{ data.transcript }}</span>
                                <ui-tooltip>
                                    <span :class="css.mrna_label" v-if="data.default || data.default_clinical">
                                        {{ (data.default && "M") || (data.default_clinical && "MP") }}
                                    </span>
                                    <template #content>
                                        {{ (data.default && "MANE Select") || (data.default_clinical && "MANE Plus Clinical ") }}
                                    </template>
                                </ui-tooltip>
                            </project-route>
                        </template>
                    </template>

                    <template #clinical="{ data }">
                        {{ data.clinical }}
                        <project-route v-if="data.clinvar" :to="`https://www.ncbi.nlm.nih.gov/clinvar/variation/${data.clinvar}/`">
                            <ui-icon :class="css.export" name="export" />
                        </project-route>
                    </template>

                    <template #disease_qty="{ data }">
                        <span>{{ data.related_diseases.length }}</span>
                        <ui-tooltip :class-extra-name="css.diseases" direction="bottom" align="start">
                            <span :class="css.add" v-if="data.related_diseases.length">+</span>
                            <template #content>
                                <span class="color-1 font-bold">{{ $t("RelatedDiseases") }}：</span>
                                <div :class="css.disease_items">
                                    <template v-for="(value, key) in data.related_diseases">
                                        <project-route :class="css.disease" :to="{ name: 'Disease', params: { id: value.id } }">
                                            {{ value.name }}
                                        </project-route>
                                    </template>
                                </div>
                            </template>
                        </ui-tooltip>
                    </template>

                    <template #references="{ data }">
                        {{ data.references }}
                        <project-route v-if="data.references > 0" :to="data.references_link">
                            <ui-icon :class="css.export" name="export" />
                        </project-route>
                    </template>
                </SearchDiagram>

                <!-- * 分页器 -->
                <UiPagination v-if="count > 20" v-model="page" :count="count" :limit="20" :skip="0" @change="methods.data()">
                    <template #items> {{ $t("Layout.PaginationDetails", { count: count, limit: 20 }) }}</template>
                </UiPagination>

                <template #aside>
                    <!-- * 标题 -->
                    <DefaultName>{{ $route.meta.name ? $t($route.meta.name) : "" }}</DefaultName>
                    <!-- * 筛选器 -->
                    <div :class="css.selectors">
                        <div :class="css.selector" v-for="(value, key) in selector.slice(0, 2)" :key="key">
                            <!-- * 全选按钮 -->
                            <ui-checkbox
                                :class="css.checkbox"
                                v-model="value.selector"
                                value=""
                                @change="methods.selectors(value)"
                                :title="value.name">
                                <h3 :class="css.name" class="font-bold font-variable color-1 ellipsis">{{ value.name }}</h3>
                            </ui-checkbox>

                            <!-- * 单选项 -->
                            <UiCheckboxGroup :class="css.checkboxs" v-model="value.result" @change="methods.selector(value)">
                                <template v-for="(opt, index) in value.options" :key="index">
                                    <ui-checkbox :class="css.checkbox" :value="opt.value" :title="opt.label">
                                        <h4 class="ellipsis font-normal">{{ opt.label }}</h4>
                                    </ui-checkbox>
                                </template>
                            </UiCheckboxGroup>
                        </div>
                    </div>
                </template>
            </ColumnLayout>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
//* 组件库
import { UiCheckbox, UiPagination, UiCheckboxGroup, UiIcon, UiSelect, UiTooltip } from "various-ui";
//* 公共组件
import { ColumnLayout, DefaultName } from "@/components/complete";
import { DefaultSearch } from "@/components/controls";
import { SearchDiagram } from "@/components/visualizations";
//* 私有公共组件
import ModuleNavigations from "../../components/navigations/index.vue";
//* 组件属性
import { useComposable } from "../src/composable";
import { useMutationOption } from "../src/options";
//* 样式
import css from "../style/index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组合函数
const { ons, refs, nodes, watchs, methods } = useComposable("mutation");
const { container } = nodes;
const { data, page, count, keyword, selector, keyword2, gene, selector2 } = refs;
//* 配置项
const option = useMutationOption();

//* 组件挂载初始化
onMounted(() => {
    methods.init();
});
//* 组件卸载前, 移除loading
onBeforeUnmount(() => watchs?.stop());
</script>
